import { Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  STAT_MOTION_MS,
  easeOutCubic,
  parseCountUp,
  prefersReducedMotion,
  renderCountUp,
  sanitizeCountUpSource,
} from '../utils/stat-motion';

/**
 * Counts a dashboard / KPI figure from 0 (or the previous value) to the formatted target.
 * Pass the same string the screen already shows (`12,5 %`, `1 234,00 Kč`, `42`).
 * `[appCountUpStatic]="true"` paints the final text with no tween (table body cells).
 */
@Directive({
  selector: '[appCountUp]',
  standalone: true,
})
export class CountUpDirective implements OnChanges, OnDestroy {
  @Input() appCountUp: string | number | null | undefined;
  /** Skip the tween and show the formatted value immediately. */
  @Input() appCountUpStatic = false;

  private rafId = 0;
  private currentValues: number[] = [];
  private hasAnimated = false;
  private reducedMotion = false;
  private mediaQuery: MediaQueryList | null = null;
  private readonly onMotionPreference = (event: MediaQueryListEvent) => {
    this.reducedMotion = event.matches;
    this.sync();
  };

  constructor(private readonly host: ElementRef<HTMLElement>) {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.mediaQuery.matches;
      this.mediaQuery.addEventListener?.('change', this.onMotionPreference);
    } else {
      this.reducedMotion = prefersReducedMotion();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appCountUp'] || changes['appCountUpStatic']) {
      this.sync();
    }
  }

  ngOnDestroy(): void {
    this.cancel();
    this.mediaQuery?.removeEventListener?.('change', this.onMotionPreference);
  }

  private sync(): void {
    this.cancel();
    const source = sanitizeCountUpSource(this.appCountUp);
    const el = this.host.nativeElement;
    if (source == null) {
      el.textContent = '';
      this.currentValues = [];
      this.hasAnimated = false;
      return;
    }

    const model = parseCountUp(source);
    const targets = model.values;
    const from =
      this.hasAnimated && this.currentValues.length === targets.length
        ? this.currentValues.slice()
        : targets.map(() => 0);
    const unchanged = from.length === targets.length && from.every((value, index) => value === targets[index]);
    if (this.appCountUpStatic || this.reducedMotion || targets.length === 0 || unchanged) {
      el.textContent = source;
      this.currentValues = targets.slice();
      this.hasAnimated = true;
      return;
    }

    const start = typeof performance !== 'undefined' ? performance.now() : 0;
    el.textContent = renderCountUp(model, from);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / STAT_MOTION_MS);
      if (t >= 1) {
        el.textContent = source;
        this.currentValues = targets.slice();
        this.hasAnimated = true;
        this.rafId = 0;
        return;
      }
      const values = from.map((origin, index) => origin + (targets[index] - origin) * easeOutCubic(t));
      this.currentValues = values;
      el.textContent = renderCountUp(model, values);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private cancel(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }
}
